// Lib
import thunk from 'redux-thunk';

// Utils
import { platformSupportLogger } from '../platform/utils/platformSupportUtils';

// Custom middleware
import { createSocketMiddleware } from '../utils/socket/middleware/createSocketMiddleware';
import undoRedoMiddleware from '../utils/undoRedo/middleware/undoRedoMiddleware';
import createAnalyticsMiddleware from '../analytics/analyticsMiddleware';
import filterStateRoutingMiddleware from './filterStateRoutingMiddleware';
import elementCountMiddleWare from '../user/elementCount/elementCountMiddleware';
import addStateToWindowMiddleware from './addStateToWindowMiddleware';
import forceElementLoadMiddleware from './forceElementLoadMiddleware';
import onboardingMiddleware from '../workspace/onboarding/onboardingMiddleware';
import cancelAttachmentUploadMiddleware from './cancelAttachmentUploadMiddleware';
import activityMiddleware from '../notifications/activityMiddleware';
import notificationsFilteringMiddleware from '../notifications/notificationsFilteringMiddleware';
import notificationsDataFetchMiddleware from '../notifications/notificationsDataFetchMiddleware';
import { bulkingMiddleware } from '../../common/store/reduxBulkingMiddleware';
import userLogoutMiddleware from './userLogoutMiddleware';
import canvasSizeMiddleware from '../canvas/size/canvasSizeMiddleware';
import addUserToActionMiddleware from './addUserToActionMiddleware';
import addMetaVersionToUpdateActionMiddleware from './addMetaVersionToUpdateActionMiddleware';
import addTokensToActionMiddleware from './addTokensToActionMiddleware';
import cloneActionMiddleware from '../element/clone/cloneActionMiddleware';
import collisionDetectionMiddleware from '../components/collision/collisionDetectionMiddleware';
import unsavedCommentsAlertMiddleware from './unsavedCommentsAlertMiddleware';
import remoteActivityMiddleware from '../remoteActivity/remoteActivityMiddleware';
import elementContentDiffUpdateActionMiddleware from './elementContentDiffUpdateActionMiddleware';
import localCacheMiddleware from '../offline/cache/localCacheMiddleware';
import { createMainStoreLoggingMiddleware } from './mainStoreLoggingMiddleware';
import visibleConnectionStatusMiddleware from '../offline/visibleConnectionStatus/visibleConnectionStatusMiddleware';
import appServerReconnectionMiddleware from '../offline/appServerConnection/appServerReconnectionMiddleware';
import persistenceMiddleware from '../offline/persistence/middleware/persistenceMiddleware';
import debugReduxActionTimeMiddleware from '../debug/performance/debugReduxActionTimeMiddleware';
import { historyMiddleware } from './history';
import scrollToElementMiddleware from '../mobile/boardsPage/editing/scrollToElementMiddleware';
import refreshModifiedBoardMiddleware from '../utils/collaboration/refreshModifiedBoardMiddleware';
import mobileEditingMiddleware from '../mobile/boardsPage/editing/mobileEditingMiddleware';
import attachedCommentMiddleware from '../element/attachedComment/attachedCommentMiddleware';
import soundMiddleware from '../sounds/soundMiddleware';
import { typingBufferMiddleware } from '../../common/tiptap/utils/typingBufferSingleton';

// Analytics
import { NewRelicCustomAttributes } from '../analytics/newRelicConstants';
import { setNewRelicCustomAttribute } from '../analytics/newRelicUtils';

// Utils
import { isGlobalDebugEnabled } from '../debug/debugUtil';
import { isLoggerDebugEnabled, LoggerComponents } from '../logger';
import { SUPPORTS_PERSISTENCE_LAYER } from '../offline/persistence/utils/persistenceLayerSupport';

// Constants
export default ({ socketManager, thunkArg = {} }) => {
    const middlewareList = [];

    middlewareList.push(thunk.withExtraArgument(thunkArg));

    middlewareList.push(mobileEditingMiddleware);
    middlewareList.push(scrollToElementMiddleware);

    if (isLoggerDebugEnabled(LoggerComponents.REDUX_LOG)) middlewareList.push(debugReduxActionTimeMiddleware);

    middlewareList.push(appServerReconnectionMiddleware);
    middlewareList.push(visibleConnectionStatusMiddleware);
    middlewareList.push(localCacheMiddleware);
    middlewareList.push(refreshModifiedBoardMiddleware);

    setNewRelicCustomAttribute(NewRelicCustomAttributes.SUPPORTS_PERSISTENCE_LAYER, !!SUPPORTS_PERSISTENCE_LAYER);

    middlewareList.push(remoteActivityMiddleware);
    middlewareList.push(cloneActionMiddleware);
    middlewareList.push(addUserToActionMiddleware);
    middlewareList.push(addMetaVersionToUpdateActionMiddleware);
    middlewareList.push(addTokensToActionMiddleware);
    middlewareList.push(userLogoutMiddleware);
    middlewareList.push(canvasSizeMiddleware(thunkArg));
    middlewareList.push(onboardingMiddleware);

    middlewareList.push(undoRedoMiddleware);

    middlewareList.push(historyMiddleware());

    middlewareList.push(cancelAttachmentUploadMiddleware);
    middlewareList.push(elementCountMiddleWare);
    middlewareList.push(filterStateRoutingMiddleware);

    middlewareList.push(createAnalyticsMiddleware());
    middlewareList.push(unsavedCommentsAlertMiddleware);

    middlewareList.push(notificationsDataFetchMiddleware);
    middlewareList.push(notificationsFilteringMiddleware);
    middlewareList.push(activityMiddleware);
    middlewareList.push(collisionDetectionMiddleware);
    middlewareList.push(attachedCommentMiddleware);
    middlewareList.push(soundMiddleware);

    middlewareList.push(typingBufferMiddleware);

    if (socketManager) {
        middlewareList.push(createSocketMiddleware(socketManager));
    }

    middlewareList.push(bulkingMiddleware());
    middlewareList.push(elementContentDiffUpdateActionMiddleware);
    middlewareList.push(forceElementLoadMiddleware);

    if (isLoggerDebugEnabled(LoggerComponents.REDUX_LOG_MAIN)) {
        middlewareList.push(createMainStoreLoggingMiddleware());
    }

    if (isGlobalDebugEnabled()) {
        middlewareList.push(addStateToWindowMiddleware());
    }

    platformSupportLogger.info(`Persistence layer - ${SUPPORTS_PERSISTENCE_LAYER ? 'enabling' : 'not enabled'}`);

    // Add this last so the action remains the same when the buildCacheOperationFromElementsAction is called
    // from here and then from within the elementSummariesSelector.ts (for memoisation)
    if (SUPPORTS_PERSISTENCE_LAYER) {
        middlewareList.push(persistenceMiddleware);
    }

    return middlewareList;
};
